import React, { useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import FlexLib from '@flex/lib';
import {
  Container,
  Grid,
  TextField,
  Backdrop,
  CircularProgress,
  Button,
  Stack,
  ToggleButtonGroup,
  ToggleButton,
  Checkbox,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import { Chat, ContentCopy, QrCode, Send } from '@mui/icons-material';
import config from '../../../constants/config';
import commonStrings from '../../../constants/commonStrings';
import autoFillMultipassFields from '../../../helpers/functions/autoFillCardFields';
import { changeFocus } from '../../../helpers/changeFocusCard';
import QRModal from './QRModal';
import PaxTerminalModal from './PaxTerminalModal';
import { getBinLookup } from '../../../services/binLookupService';
import invoicesService from '../../../services/invoicesService';
import customersService from '../../../services/customersService';
import transactionsService from '../../../services/transactionsService';
import { FlexAlerts, AlertSeverityTypes } from '../../../components/Message/Alerts/Alerts';
import AmountTextField from '../../../components/FeeDisplay/AmountTextField';
import TsepCardV2 from '../../../components/CardForm/TsepCard';
import PaytraceCard from '../../../components/CardForm/PaytraceCard';
import { BackButton } from '../../../components/Buttons';
import ACHForm from '../../../components/ACHForm/ACHForm';
import AddressFieldsDropDown from '../../../components/AddressFieldsDropDown/AddressFieldsDropDown2';
import FeeAmountTextField from '../../../components/FeeDisplay/FeeAmountTextField';
import SelectAccount from '../../../components/SelectAccount/SelectAccount';
import CreatableAutocomplete from '../../../components/CreatableAutocomplete';
import virtualTerminalConstants from '../../../constants/virtualTerminalConstants';
const { PAY_BUTTON_LABELS, PAY_TYPE_DEFAULTS, REDIRECT_PATHS, NUM_ITEMS_IN_LIST, REDIRECT_DELAY_MS } =
  virtualTerminalConstants;

const feeService = new FlexLib.services.FeeService(console);
const { formatCurrency, formatCurrencyInput } = FlexLib.utils.feeService;
const mapDatabaseFeeSettings = FlexLib.mappers.feeService.mapDatabaseFeeSettings;

const CreditTab = () => {
  const [paymentGateway, setPaymentGateway] = useState('');

  // UI Control
  const [isLoading, setIsLoading] = useState(false);
  const [isQRCodeModalOpen, setIsQRCodeModalOpen] = useState(false);
  const [paymentTypeToggle, setPaymentTypeToggle] = useState(commonStrings.card);
  const [payButtonLabel, setPayButtonLabel] = useState(PAY_BUTTON_LABELS.card);
  const [submitPayIsDisabled, setSubmitPayIsDisabled] = useState(false);
  const [messages, setMessages] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({
    invoice: false,
    customer: false,
    baseAmount: false,
    totalAmount: false,
  });
  const billingAddressFormValidationRef = React.useRef(null);
  const shippingAddressFormValidationRef = React.useRef(null);
  const history = useHistory();
  const [redirectTo, setRedirectTo] = useState('');

  // Settings
  const [achConfig, setAchConfig] = useState({
    isACHEnabled: false,
    confirmAccountNumber: false,
    isACHPaymentVaultEnabled: false,
    isExtraFieldsRequired: false,
  });
  const [invoiceListConfig, setInvoiceListConfig] = useState({
    label: 'Invoice Number',
    required: false,
    autoGenerated: false,
  });
  const [isPhoneNumberEnabled, setIsPhoneNumberEnabled] = useState(false);
  const [isPaxTransactionsEnabled, setIsPaxTransactionsEnabled] = useState(false);
  const [paxTerminals, setPaxTerminals] = useState([]);
  const [isDualPricingView, setIsDualPricingView] = useState(false);

  const [userData, setUserData] = useState({});
  const [paymentType, setPaymentType] = useState('');
  const [calculationResult, setCalculationResult] = useState({});
  const [displayCalculationResult, setDisplayCalculationResult] = useState({}); // same as calculation result unless dual pricing
  const [paylinkHash, setPaylinkHash] = useState('');

  // Transaction Details
  const [invoicesList, setInvoicesList] = useState([]); // list of invoice options
  const [selectedInvoice, setSelectedInvoice] = useState(null); // selected existing invoice
  const [invoiceNumber, setInvoiceNumber] = useState(''); // new invoice number attached to transaction
  const [customersList, setCustomersList] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerName, setCustomerName] = useState('');
  const [baseAmount, setBaseAmount] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const defaultAddress = {
    addressOne: '',
    addressTwo: '',
    city: '',
    state: '',
    zip: '',
  };
  const [billingAddress, setBillingAddress] = useState(defaultAddress);
  const [shippingAddress, setShippingAddress] = useState(defaultAddress);
  const [notes, setNotes] = useState('');

  const [binCodesResponse, setBinCodesResponse] = useState({ bin: '', card: '', type: '' });
  const [tsepResponse, setTsepResponse] = useState({});

  // Payment data
  const [cardDisplayData, setCardDisplayData] = useState({
    showCardInput: true,
    accountType: '',
    maskedCardNumber: '',
    variant: 'block',
    showSaveCheckbox: true,
  });
  const [card, setCard] = useState({});
  const [cardErrors, setCardErrors] = useState({});
  const cardRef = useRef();
  const cscCardRef = useRef();
  const expiryDateRef = useRef();
  const [reloadTsepCount, setReloadTsepCount] = useState(0); // increment to reload tsep fields
  const triggerTsepReload = () => setReloadTsepCount((prev) => prev + 1);

  const initialAchDetails = {
    accountName: '',
    accountNumber: '',
    routingNumber: '',
    accountType: '',
    bankName: '',
  };
  const [achDetails, setAchDetails] = useState(initialAchDetails);
  const achFormValidationRef = useRef(null);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState({});
  const resetPaymentMethodIfNeeded = () => {
    setSelectedPaymentMethod((prev) => {
      if (prev && typeof prev === 'object') {
        return Object.keys(prev).length ? {} : prev;
      }
      return {};
    });
  };
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [isPaymentMethodToBeSaved, setIsPaymentMethodToBeSaved] = useState(false);

  useEffect(() => {
    getSessionData();
  }, []);

  useEffect(() => {
    if (paymentGateway === commonStrings.multipass) {
      document.addEventListener('tsepEventUpdate', handleTsepEventUpdated);
    } else if (paymentGateway === commonStrings.paytrace) {
      let locationDetails = localStorage.getItem('locationPaydetails');
      locationDetails = JSON.parse(locationDetails) || {};
      if (locationDetails?.paytraceKey) {
        const localUrl = config.BASEURL + 'getPemFile?fileName=';
        window.paytrace.setKeyAjax(localUrl + locationDetails?.paytraceKey);
      }
    }

    return () => {
      document.removeEventListener('tsepEventUpdate', handleTsepEventUpdated);
    };
  }, [paymentGateway]);

  useEffect(() => {
    calculateFees();
  }, [baseAmount, userData?.settings, billingAddress?.state, paymentType]);

  useEffect(() => {
    setMessages([]);
    if (selectedInvoice?.value) {
      // Selecting an existing invoice
      setInvoiceNumber(selectedInvoice?.invoiceNumber);
      setBaseAmount(formatCurrencyInput(selectedInvoice.amount) ?? '');
      setNotes(selectedInvoice.publicNotes);
      if (selectedInvoice?.customerId) {
        // Grabs customer data for existing invoices - new invoices will have customer data pulled when created.
        const customer = customersList.find((x) => x.value === selectedInvoice.customerId);
        if (customer) {
          setSelectedCustomer(customer);
        }
        setCustomerEmail(selectedInvoice.email ?? customer?.email ?? '');
        setCustomerPhone(selectedInvoice.phone ?? customer?.phone ?? '');
      }
    } else if (selectedInvoice?.label) {
      // Adding a new invoice
      setPaylinkHash('');
      invoicesService
        .checkIfInvoiceNumberIsUse(selectedInvoice?.label)
        .then((response) => {
          const isInUse = response?.data?.isInUse;
          if (typeof isInUse === 'boolean') {
            if (isInUse) {
              setMessages([{ severity: AlertSeverityTypes.error, message: response?.data?.message }]);
            } else {
              setInvoiceNumber(selectedInvoice?.label);
            }
          }
        })
        .catch((error) => {
          setMessages([
            {
              severity: AlertSeverityTypes.error,
              message: error?.response?.data?.message ?? 'Could not determine whether invoice number is valid.',
            },
          ]);
        });
    } else {
      setInvoiceNumber('');
      setPaylinkHash('');
    }
  }, [selectedInvoice]);

  useEffect(() => {
    if (selectedCustomer?.value) {
      setCustomerName(selectedCustomer?.label);
      setBillingAddress(selectedCustomer.billingAddress ?? defaultAddress);
      setShippingAddress(selectedCustomer.shippingAddress ?? defaultAddress);
      if (!selectedInvoice?.value) {
        setCustomerPhone(selectedCustomer.phone);
        setCustomerEmail(selectedCustomer.email);
      }

      getPaymentVault();
    } else {
      if (selectedCustomer?.label) {
        setCustomerName(selectedCustomer?.label);
      }
      setBillingAddress(defaultAddress);
      setShippingAddress(defaultAddress);
      resetPaymentMethodIfNeeded();
      setSavedPaymentMethods([]);

      if (!selectedInvoice?.value) {
        setCustomerPhone('');
        setCustomerEmail('');
      }
    }
  }, [selectedCustomer]);

  useEffect(() => {
    if (!selectedPaymentMethod?.gatewayPaymentMethodId) {
      setCardDisplayData({
        showCardInput: true,
        accountType: '',
        maskedCardNumber: '',
        variant: 'block',
        showSaveCheckbox: true,
      });
      setAchDetails(initialAchDetails);
      setPaymentType(paymentTypeToggle === commonStrings.card ? commonStrings.debit : commonStrings.ach);
      return;
    }

    if (paymentTypeToggle === commonStrings.card) {
      triggerTsepReload();
    }
    setPaymentType(selectedPaymentMethod?.paymentType ?? '');
    setIsPaymentMethodToBeSaved(false); // stop trying to save once an already saved one is selected.
    if ([commonStrings.credit, commonStrings.debit].includes(selectedPaymentMethod?.paymentType?.toLowerCase())) {
      setCardDisplayData({
        showCardInput: false,
        accountType: selectedPaymentMethod.accountType,
        maskedCardNumber: selectedPaymentMethod.maskedAccountNumber,
      });
    } else if (selectedPaymentMethod?.paymentType?.toLowerCase() === commonStrings.ach) {
      setAchDetails({
        accountName: selectedPaymentMethod.accountName?.trim(),
        accountNumber: selectedPaymentMethod.maskedAccountNumber,
        routingNumber: selectedPaymentMethod.routingNumber,
        accountType: selectedPaymentMethod.accountType,
        bankName: selectedPaymentMethod.bankName,
      });
    }
  }, [selectedPaymentMethod]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [messages]);

  useEffect(() => {
    let timeoutId;
    if (redirectTo) {
      timeoutId = setTimeout(() => {
        history.push(redirectTo);
      }, REDIRECT_DELAY_MS);
    }

    return () => clearTimeout(timeoutId);
  }, [redirectTo]);

  useEffect(() => {
    // Disables one time card payment submission unless tsep response status is passing.
    if (
      paymentGateway === commonStrings.multipass &&
      !selectedPaymentMethod?.gatewayPaymentMethodId &&
      paymentType !== commonStrings.ach &&
      tsepResponse?.status !== 'PASS'
    ) {
      setSubmitPayIsDisabled(true);
    } else {
      setSubmitPayIsDisabled(false);
    }
  }, [paymentGateway, selectedPaymentMethod, paymentType, tsepResponse]);

  const getSessionData = () => {
    let location = localStorage.getItem('locationPaydetails');
    location = JSON.parse(location) || {};

    setPaymentGateway(location?.gateway);

    setAchConfig({
      isACHEnabled: location?.ACH,
      confirmAccountNumber: location?.confirmAccountNumber,
      isACHPaymentVaultEnabled: location?.ACHPaymentVault,
      isExtraFieldsRequired: location?.ACHFieldDisplay,
    });

    setIsPaxTransactionsEnabled(location?.paxTransactionEnable);
    setPaxTerminals([...(location?.paxTerminals || [])]);

    const customInvoiceField = location?.customFields?.find((field) => field?.enabled === true);
    if (customInvoiceField) {
      setInvoiceListConfig({
        label: customInvoiceField?.value,
        required: customInvoiceField?.required,
        autoGenerated: customInvoiceField?.autoGenerate,
      });
    }

    if (customInvoiceField?.autoGenerate) {
      invoicesService
        .generateUniqueInvoiceNumber()
        .then((response) => {
          const generatedInvoiceNumber = response?.data?.invoiceNumber;
          if (generatedInvoiceNumber) {
            setSelectedInvoice({ label: generatedInvoiceNumber });
            setInvoiceNumber(generatedInvoiceNumber);
          }
        })
        .catch((error) => {
          setMessages([{ severity: AlertSeverityTypes.error, message: error?.response?.data?.message }]);
        });
    }

    let user = localStorage.getItem('user');
    user = JSON.parse(user) || {};
    setUserData(user);
    setIsDualPricingView(user?.settings?.DualFeeEnabled);
    setIsPhoneNumberEnabled(user?.settings?.isPhoneNumber);

    if (user?._id && location?._id) {
      getInvoicesList(user, location?._id);
      getCustomersList(user);
    } else {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Could not retrieve customers and invoices. Try reloading the page.',
        },
      ]);
    }
  };

  const concatenateCustomerName = (firstName, lastName) => {
    return `${firstName ?? ''}${firstName && lastName ? ' ' : ''}${lastName ?? ''}`;
  };

  const formatInvoicesList = (list) => {
    const filteredInvoices = list.slice(0, NUM_ITEMS_IN_LIST);

    // transaction number - date - status - customer name - amount
    const formattedInvoices = filteredInvoices.map((invoice) => {
      let amount = invoice.amount?.toString() ?? '';

      const customerName = concatenateCustomerName(invoice.customerId?.firstName, invoice.customerId?.lastName);

      const formatted = {
        label: `${invoice.invoice} - ${invoice.STATUS} - ${customerName} - ${formatCurrency(amount)}`,
        value: invoice._id,
        amount,
        customerName,
        customerId: invoice.customerId?._id ? invoice.customerId._id : invoice.customerId,
        email: invoice.customerId?.email ?? invoice.email ?? '',
        phone: invoice.customerId?.phone ?? invoice.phone ?? '',
        notes: invoice.publicNotes ?? '',
        invoiceNumber: invoice.invoice,
        invoiceId: invoice._id,
      };
      return formatted;
    });

    return formattedInvoices;
  };

  const getInvoicesList = (userData, locationId) => {
    if (!userData?._id || !userData?.company?._id || !locationId) {
      setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not retrieve invoices.' }]);
      return;
    }

    setIsLoading(true);
    invoicesService
      .getPendingInvoicesList({
        companyId: userData?.company?._id,
        userId: userData?._id,
        locationId,
      })
      .then((response) => {
        if (response?.data?.response) {
          setInvoicesList(formatInvoicesList(response.data.response));
        }
      })
      .catch((_error) => {
        setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not retrieve invoices.' }]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const formatCustomersList = (list) => {
    const formattedCustomers = list.map((customer) => {
      const customerName = concatenateCustomerName(customer?.firstName, customer?.lastName);

      const formatted = {
        label: customerName,
        value: customer.id,
        phone: customer.phone ?? '',
        email: customer.email ?? '',
        billingAddress: customer.billingAddress || defaultAddress,
        shippingAddress: customer.shippingAddress || defaultAddress,
      };
      return formatted;
    });

    return formattedCustomers;
  };

  const getCustomersList = (userData) => {
    if (!userData?._id || !userData?.company) {
      setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not retrieve customers.' }]);
      return;
    }
    setIsLoading(true);
    customersService
      .getCustomersList({ onlyActiveCustomers: true })
      .then((response) => {
        if (response?.data?.response) {
          setCustomersList(formatCustomersList(response.data.response));
        }
      })
      .catch((_error) => {
        setCustomersList([]);
        setMessages([{ severity: AlertSeverityTypes.error, message: 'Could not retrieve customers.' }]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const getPaymentVault = () => {
    resetPaymentMethodIfNeeded();
    setSavedPaymentMethods([]);
    setIsLoading(true);
    if (selectedCustomer?.value?.length > 0) {
      customersService
        .getPaymentMethods(selectedCustomer.value)

        .then((response) => {
          if (Array.isArray(response.data?.response)) {
            setSavedPaymentMethods([...response.data.response]);
            if (response.data.response?.length > 0 && paymentTypeToggle === commonStrings.card) {
              const legacyCard = response.data.response?.find((x) =>
                [commonStrings.credit, commonStrings.debit].includes(x.paymentType?.toLowerCase())
              );

              if (legacyCard && typeof legacyCard === 'object') {
                setSelectedPaymentMethod(legacyCard);
              }
            }
          }
        })
        .catch((_error) => {
          setMessages([
            { severity: AlertSeverityTypes.error, message: 'Could not get saved payment methods for this customer.' },
          ]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  const calculateFees = () => {
    const _baseAmount = baseAmount || '0.00';

    let result;
    try {
      if (!userData?.settings) {
        return {};
      }

      // Map database fees to fee config
      const { feeConfig, options } = mapDatabaseFeeSettings(userData?.settings ?? {});
      result = feeService.getCalculatedFees(
        _baseAmount,
        feeConfig,
        options,
        paymentType?.toLowerCase(),
        billingAddress?.state
      );

      setCalculationResult(result);

      if (options.dualPricing) {
        result = feeService.getDualPricingCalculatedFees(
          _baseAmount,
          feeConfig,
          options,
          paymentType?.toLowerCase(),
          billingAddress?.state
        );
      }
      setDisplayCalculationResult(result);
    } catch (e) {
      if (typeof setError === 'function' && e instanceof Error) {
        let erMsg = e.message;
        if (/fails to match the required pattern/.test(e?.message)) {
          erMsg =
            'Please verify values in Fee Settings. Percent fees can have 5 decimal places. Flat fees can have 2 decimal places.';
        }

        setMessages({
          severity: AlertSeverityTypes.error,
          message: `A fee calculation error has occurred: ${erMsg} If the error persists, contact techsupport@titaniumpayments.com or call 877-972-0700.`,
        });
      }
    }

    return result;
  };

  const getBinCodesResponse = async (cardBin) => {
    if (cardBin?.length >= 6) {
      getBinLookup(cardBin)
        .then((response) => {
          if (response?.paymentType) {
            setPaymentType(response.paymentType);
          }
          setBinCodesResponse(response?.binCodesResponse);
        })
        .catch((_error) => {
          setMessages([
            { severity: AlertSeverityTypes.error, message: 'Could not complete bin lookup. Please contact support.' },
          ]);
        });
    }
  };

  const handleChangePaytraceCard = (event) => {
    let { id, value } = event.target;

    setCardErrors({ cardNumber: false, cardExpiryDate: false, cardCsc: false });

    // Validate values
    if (id === 'cardNumber') {
      const cleanedNumber = value?.replace(/[_ ]/g, '');
      if (cleanedNumber.length >= 6) {
        getBinCodesResponse(cleanedNumber.slice(0, 6));
      }
    }
    if (value.length === 16 && value[value.length - 1] === '_' && id === 'cardNumber' && value[0] === '3') {
      value = value.slice(0, -1);
    }
    if (id === 'cardExpiryDate') {
      if (!/^(0[1-9]|1[0-2])([/-])?(\d{4})$/gm.test(value)) {
        setCardErrors((prevState) => ({ ...prevState, cardExpiryDate: true }));
      } else {
        setCard((prevState) => ({ ...prevState, cardExpiryDate: value }));
      }
    }
    changeFocus(id, value, expiryDateRef.current, cscCardRef.current);
  };

  const handleTsepEventUpdated = (event) => {
    const eventTsep = event.detail;
    setTsepResponse({ status: 'FAIL', tsepToken: '' });
    setIsPaymentMethodToBeSaved(false);
    if (eventTsep.eventType === 'FieldValidationErrorEvent') {
      setMessages([{ severity: AlertSeverityTypes.error, message: eventTsep?.event?.message }]);
    } else if (eventTsep.eventType === 'TokenEvent' && eventTsep.event.status === 'PASS') {
      setTsepResponse({ ...eventTsep.event });
      setMessages([]);
    } else if (eventTsep.eventType === 'TokenEvent' && eventTsep.event.status === 'FAILURE') {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: `Please reload page to try again. ${eventTsep?.event?.message}`,
        },
      ]);
    } else if (eventTsep.eventType === 'FocusOutEvent' && eventTsep.event.fieldName === 'tsep-cardNum') {
      const cardNumberElement = document.getElementById('tsep-cardNum');
      if (cardNumberElement) {
        const cardNumberValue = cardNumberElement?.value.replace(/\s/g, '');
        getBinCodesResponse(cardNumberValue);
      }
    } else if (
      eventTsep.eventType === 'ErrorEvent' &&
      eventTsep.event.status === 'FAIL' &&
      eventTsep.event.responseCode === 'TSEPERR911'
    ) {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Could not connect to Multipass. Please reload page to try again or contact support.',
        },
      ]);
    }
  };

  const handleChangeACH = (event) => {
    let { id, value } = event.target;

    setAchDetails((prevAch) => ({
      ...prevAch,
      [id]: value,
    }));
  };

  const getOneTimeCard = () => {
    let oneTimeCard = {
      encryptedCard: '',
      encryptedCSC: '',
      expirationDate: '',
    };

    if (paymentGateway === commonStrings.multipass && tsepResponse?.status === 'PASS') {
      oneTimeCard.encryptedCard = tsepResponse.tsepToken;
      oneTimeCard.encryptedCSC = tsepResponse.cvv2;
      oneTimeCard.expirationDate = tsepResponse.expirationDate;
    } else if (paymentGateway === commonStrings.paytrace) {
      window.paytrace.submitEncrypted('#DemoFormTerminal');
      oneTimeCard.encryptedCard = localStorage.getItem('0');
      oneTimeCard.encryptedCSC = localStorage.getItem('1');
      oneTimeCard.expirationDate = card?.cardExpiryDate;
      localStorage.removeItem('0');
      localStorage.removeItem('1');
    }

    return oneTimeCard;
  };

  const getPaymentDetails = (newSavedPaymentMethod = {}) => {
    let isValid = true;
    let paymentType = '';
    let paymentDetails = {};

    let savedPaymentMethod = selectedPaymentMethod;
    if (newSavedPaymentMethod.gatewayPaymentMethodId || newSavedPaymentMethod.paymentMethodId) {
      savedPaymentMethod = newSavedPaymentMethod;
    }

    switch (paymentTypeToggle) {
      case commonStrings.cash:
        paymentType = commonStrings.cash;
        break;
      case commonStrings.ach:
        paymentType = commonStrings.ach;
        if (
          achConfig.isACHPaymentVaultEnabled &&
          savedPaymentMethod?.paymentType?.toLowerCase() === commonStrings.ach
        ) {
          // Vault
          paymentDetails = {
            paymentMethodId: savedPaymentMethod.paymentMethodId,
            maskedAccountNumber: savedPaymentMethod.maskedAccountNumber,
            accountName: savedPaymentMethod.accountName,
            accountType: savedPaymentMethod.accountType,
            bankName: savedPaymentMethod.bankName,
          };
        } else if (achFormValidationRef.current?.validate()) {
          // One time
          paymentDetails = {
            accountName: achDetails.accountName?.trim(),
            accountNumber: achDetails.accountNumber,
            routingNumber: achDetails.routingNumber,
            maskedAccountNumber: achDetails.accountNumber?.slice(-4),
            accountType: achDetails.accountType,
            bankName: achDetails.bankName,
          };
        } else {
          isValid = false;
        }
        break;
      case commonStrings.card:
        if ([commonStrings.credit, commonStrings.debit].includes(savedPaymentMethod?.paymentType?.toLowerCase())) {
          // Vault
          paymentType = savedPaymentMethod.paymentType?.toLowerCase();
          paymentDetails = {
            customerId: selectedCustomer?.value,
            gatewayPaymentMethodId: savedPaymentMethod.gatewayPaymentMethodId,
            binCodesResponse: savedPaymentMethod.binCodesResponse,
            cardNetwork: savedPaymentMethod.cardNetwork,
            maskedAccountNumber: savedPaymentMethod.maskedAccountNumber,
            accountName: customerName?.trim(),
          };
        } else {
          // One time
          const { encryptedCard, encryptedCSC, expirationDate } = getOneTimeCard();

          if (!encryptedCard) {
            isValid = false;
          }

          paymentType = binCodesResponse?.type?.toLowerCase();
          paymentDetails = {
            accountName: customerName?.trim(),
            cardNetwork: binCodesResponse?.card,
            binCodesResponse: binCodesResponse,
            encryptedCard,
            encryptedCSC,
            expirationDate,
          };
        }
        break;
      default:
        isValid = false;
        break;
    }

    if (!isValid || !paymentType) {
      setMessages((prevState) => [
        { severity: AlertSeverityTypes.error, message: 'No valid payment method for request.' },
        ...prevState,
      ]);
      return {};
    }
    paymentDetails.paymentType = paymentType;
    return paymentDetails;
  };

  const saveCustomer = async () => {
    let customer = {};
    let newPaymentMethod = {};
    let paymentMethods = [];
    if (isPaymentMethodToBeSaved) {
      const paymentDetails = getPaymentDetails();
      if (!paymentDetails?.paymentType) {
        return { customer, newPaymentMethod };
      }

      paymentMethods = [paymentDetails];
    }

    const firstName = customerName?.trim();
    if (!selectedCustomer?.value && firstName) {
      const create = {
        firstName,
        email: customerEmail,
        phone: customerPhone,
        billingAddress: {
          addressOne: billingAddress?.addressOne,
          addressTwo: billingAddress?.addressTwo,
          city: billingAddress?.city,
          state: billingAddress?.state,
          zip: billingAddress?.zip,
        },
        shippingAddress: {
          addressOne: shippingAddress?.addressOne,
          addressTwo: shippingAddress?.addressTwo,
          city: shippingAddress?.city,
          state: shippingAddress?.state,
          zip: shippingAddress?.zip,
        },
        paymentMethods,
      };

      try {
        const response = await customersService.createCustomer(create);
        if (response?.data?.customer?.id) {
          customer = response?.data?.customer;
        }
      } catch (error) {
        if (error?.response?.data?.message) {
          setMessages((prevState) => [
            { severity: AlertSeverityTypes.error, message: error?.response?.data?.message },
            ...prevState,
          ]);
        } else {
          setMessages((prevState) => [
            { severity: AlertSeverityTypes.error, message: 'Customer could not be created.' },
            ...prevState,
          ]);
        }
      }
    } else if (selectedCustomer?.value && isPaymentMethodToBeSaved) {
      // Update only if payment method is updated
      const update = {
        billingAddress: {
          addressOne: billingAddress?.addressOne,
          addressTwo: billingAddress?.addressTwo,
          city: billingAddress?.city,
          state: billingAddress?.state,
          zip: billingAddress?.zip,
        },
        paymentMethods,
      };

      try {
        const response = await customersService.updateCustomer(selectedCustomer?.value, update);
        if (response?.data?.customer?.id) {
          customer = response?.data?.customer;
        }
      } catch (error) {
        if (error?.response?.data?.message) {
          setMessages((prevState) => [
            { severity: AlertSeverityTypes.error, message: error?.response?.data?.message },
            ...prevState,
          ]);
        } else {
          setMessages((prevState) => [
            { severity: AlertSeverityTypes.error, message: 'Customer could not be updated with payment method.' },
            ...prevState,
          ]);
        }
      }
    } else if (selectedCustomer?.value) {
      // No update needed, just get customer data
      return {
        customer: { id: selectedCustomer.value, email: selectedCustomer.email, phone: selectedCustomer.phone },
        newPaymentMethod,
      };
    } else {
      return { customer, newPaymentMethod };
    }

    let newSelectedCustomer = {};
    if (customer?.id) {
      const customerName = concatenateCustomerName(customer.firstName, customer.lastName);
      newSelectedCustomer = {
        label: customerName,
        value: customer.id,
        phone: customer.phone,
        email: customer.email,
        billingAddress: customer.billingAddress,
        shippingAddress: customer.shippingAddress,
      };

      if (isPaymentMethodToBeSaved) {
        newPaymentMethod = customer.paymentMethods?.find((m) => m.isNew);
        if (!newPaymentMethod) {
          setMessages([
            { severity: AlertSeverityTypes.error, message: 'Payment method did not save. Please contact support.' },
          ]);
          return { customer: {}, newPaymentMethod: {} };
        }
        setSelectedPaymentMethod(newPaymentMethod);
      }
      getCustomersList(userData);
    }

    setSelectedCustomer(newSelectedCustomer);
    return { customer, newPaymentMethod };
  };

  /**
   * Process a transaction without an invoice - invoice number is just used for the ORDERID
   * Instead of creating an invoice to pay it
   */
  const processPaymentWithoutInvoice = async () => {
    setIsLoading(true);

    // Process customer first to save payment methods
    // Then get payment details - updated with any selected new save method

    // CreditTab only allows transactions connected to a customer
    const newCustomer = await saveCustomer();
    if (!newCustomer?.customer?.id) {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.error,
          message: 'Payment could not be processed. Could not save customer.',
        },
        ...prevState,
      ]);
      setIsLoading(false);
      return false;
    }
    const paymentDetails = getPaymentDetails(newCustomer.newPaymentMethod);
    if (!paymentDetails?.paymentType) {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.error,
          message: 'Payment could not be processed. Missing payment method.',
        },
        ...prevState,
      ]);
      setIsLoading(false);
      return false;
    }

    const payload = {
      source: 'Virtual Terminal',
      baseAmount,
      customer: {
        id: newCustomer.customer.id,
        email: customerEmail,
        phone: customerPhone,
      },
      orderId: invoiceNumber,
      billingAddress,
      shippingAddress,
      notes,
      paymentMethod: paymentDetails,
    };

    setIsLoading(true); // Reset loading to accomodate loading handling within session data retrievals
    try {
      const response = await transactionsService.runTransaction(payload);
      if (response?.data?.transactionId) {
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Payment successful!' }]);
        setRedirectTo(REDIRECT_PATHS.TRANSACTIONS);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        setMessages([{ severity: AlertSeverityTypes.error, message: error?.response?.data?.message }]);
      } else {
        setMessages([
          {
            severity: AlertSeverityTypes.error,
            message: 'Payment could not be processed. Please contact support.',
          },
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Pay an existing invoice
   */
  const processPaymentOnInvoice = async (invoiceId) => {
    setIsLoading(true);

    // CreditTab only allows transactions connected to a customer
    const newCustomer = await saveCustomer();
    if (!newCustomer?.customer?.id) {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.error,
          message: 'Payment could not be processed. Could not save customer.',
        },
        ...prevState,
      ]);
      setIsLoading(false);
      return false;
    }
    const paymentDetails = getPaymentDetails(newCustomer.newPaymentMethod);
    if (!paymentDetails?.paymentType) {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.error,
          message: 'Payment could not be processed. Missing payment method.',
        },
        ...prevState,
      ]);
      setIsLoading(false);
      return false;
    }

    // Map values to Invoice payment method and delete unused values
    const paymentMethod = {
      paymentMethodId: paymentDetails.paymentMethodId,
      gatewayPaymentMethodId: paymentDetails.gatewayPaymentMethodId,
      accountNumber: paymentDetails.accountNumber,
      routingNumber: paymentDetails.routingNumber,
      accountName: paymentDetails.accountName?.trim(),
      bankAccountType: paymentDetails.accountType,
      bankName: paymentDetails.bankName,
      encrypted_csc: paymentDetails.encryptedCSC,
      encrypted_number: paymentDetails.encryptedCard,
      cardExpiryDate: paymentDetails.expirationDate,
      cardType: paymentDetails.cardNetwork,
      creditOrDebit: [commonStrings.debit, commonStrings.credit].includes(paymentDetails.paymentType)
        ? paymentDetails.paymentType?.toUpperCase()
        : null,
      binCodesResponse: paymentDetails.binCodesResponse,
    };
    for (const key in paymentMethod) {
      if (paymentMethod[key] === null || paymentMethod[key] === undefined) {
        delete paymentMethod[key];
      }
    }

    const payload = {
      invoiceNumber: selectedInvoice?.invoiceNumber,
      baseAmount,
      customerId: newCustomer.customer.id,
      email: customerEmail,
      phone: customerPhone,
      billingAddress,
      shippingAddress,
      notes: {
        publicNotes: notes,
      },
      paymentDetails: {
        paymentType: paymentDetails.paymentType,
        paymentMethod,
      },
    };

    setIsLoading(true); // Reset loading to accomodate loading handling within session data retrievals
    try {
      const response = await invoicesService.updateInvoice(invoiceId, payload, {
        sendEmail: false,
        sendText: false,
        payInvoice: true,
      });
      if (response?.data?.invoiceId) {
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Invoice updated and payment processed!' }]);
        setRedirectTo(REDIRECT_PATHS.TRANSACTIONS);
      }
    } catch (error) {
      if (error?.response?.data?.message) {
        setMessages([{ severity: AlertSeverityTypes.error, message: error?.response?.data?.message }]);
      } else {
        setMessages([
          {
            severity: AlertSeverityTypes.error,
            message: 'Payment could not be processed. Please contact support.',
          },
        ]);
      }
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Used for creating or updating an invoice when a pay link or QR code is generated, emailed, or texted.
   * Cannot be used to pay an invoice.
   * @param {boolean} sendText
   * @param {boolean} sendEmail
   * @returns
   */
  const saveInvoice = async (sendText = false, sendEmail = false) => {
    // Validate

    if (!areRequiredFieldsValid(true)) {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Missing required data.',
        },
      ]);
      return;
    }

    const newCustomer = await saveCustomer();
    if (!newCustomer?.customer?.id) {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.error,
          message: 'Could not save customer.',
        },
        ...prevState,
      ]);
      setIsLoading(false);
      return false;
    }

    const newMessages = [];
    const payload = {
      invoiceNumber: invoiceNumber,
      baseAmount,
      customerId: newCustomer.customer.id,
      email: customerEmail,
      phone: customerPhone,
      billingAddress,
      shippingAddress,
      notes: {
        publicNotes: notes,
      },
    };

    // If invoice isn't required and no invoice number required, inform user that it will be generated.
    if (!selectedInvoice?.invoiceId && !invoiceNumber && !invoiceListConfig.required) {
      delete payload.invoiceNumber;
      newMessages.push({
        severity: AlertSeverityTypes.info,
        message: `${invoiceListConfig.label} will be generated for this invoice.`,
      });
    }

    setIsLoading(true);

    let invoice = {};
    let isError = false;

    // Update selected invoice
    if (selectedInvoice?.invoiceId) {
      try {
        const response = await invoicesService.updateInvoice(selectedInvoice.invoiceId, payload, {
          sendText,
          sendEmail,
        });
        if (response?.data?.message) {
          newMessages.push();
          setMessages([{ severity: AlertSeverityTypes.success, message: response?.data?.message }]);
        }

        if (response?.data?.invoice) {
          invoice = response?.data?.invoice;
        }
      } catch (error) {
        isError = true;
        if (error?.response?.data?.invoice) {
          invoice = error?.response?.data?.invoice;
        }

        if (error?.response?.data?.message) {
          setMessages([{ severity: AlertSeverityTypes.error, message: error.response?.data?.message }]);
        } else {
          setMessages([
            { severity: AlertSeverityTypes.error, message: 'Invoice could not be updated. Please contact support.' },
          ]);
        }
      } finally {
        setIsLoading(false);
      }
    } else {
      // Create new invoice
      let invoiceId;
      try {
        const response = await invoicesService.createInvoice(payload, { sendText, sendEmail });
        if (response?.data?.invoiceId) {
          invoiceId = response.data.invoiceId;
          getInvoicesList(userData, response?.data?.locationId);
          newMessages.push({ severity: AlertSeverityTypes.success, message: response.data.message });
          setMessages(newMessages);
          invoice = response?.data?.invoice;
        }
      } catch (error) {
        isError = true;
        if (error?.response?.data?.invoiceId) {
          invoiceId = error.response.data?.invoiceId;
          invoice = error.response.data?.invoice;
        }
        if (error?.response?.data?.message) {
          setMessages([{ severity: AlertSeverityTypes.error, message: error.response?.data?.message }]);
        } else {
          setMessages([
            { severity: AlertSeverityTypes.error, message: 'Invoice could not be created. Please contact support.' },
          ]);
        }
      } finally {
        setIsLoading(false);
      }

      if (invoiceId) {
        setSelectedInvoice({
          label: invoice?.invoiceNumber,
          value: invoiceId,
          amount: invoice?.baseAmount?.toString(),
          customerId: invoice?.customerId,
          notes: invoice?.notes?.publicNotes,
          invoiceNumber: invoice?.invoiceNumber,
          invoiceId: invoiceId,
          email: invoice?.email,
          phone: invoice?.phone,
        });
      }
    }

    // Save invoice to selected invoice if returned, but make sure calling function knows an error occurred
    if (isError) {
      return {};
    }
    return invoice;
  };

  /**
   * Validates basic fields based on what's required. Updates error state for component missing.
   * @param {boolean} isSavingInvoice - removes some required fields due to just saving invoice and not processing payment
   * @returns {boolean}
   */
  const areRequiredFieldsValid = (isSavingInvoice = false) => {
    let isValid = true;

    const newFieldErrors = {
      baseAmount: !baseAmount,
      customer: !customerName,
      invoice: invoiceListConfig.required && !invoiceNumber,
      totalAmount: !calculationResult?.totalAmount,
    };

    setFieldErrors(newFieldErrors);

    Object.values(newFieldErrors).forEach((value) => {
      isValid = isValid && !value;
    });

    if (!isSavingInvoice) {
      // Address validation updates own error state
      // Only applies if addresses are made visible
      if (billingAddressFormValidationRef.current) {
        isValid = isValid && billingAddressFormValidationRef.current.validate();
      }

      if (shippingAddressFormValidationRef.current) {
        isValid = isValid && shippingAddressFormValidationRef.current.validate();
      }
    }

    return isValid;
  };

  const handleGenerateQRCode = async () => {
    const invoice = await saveInvoice();

    if (!invoice?.hash) {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.error,
          message: 'QR code could not be generated.',
        },
        ...prevState,
      ]);
      return;
    }

    setPaylinkHash(invoice.hash);

    // open QR code modal
    setIsQRCodeModalOpen(true);
  };

  const handleGeneratePaylink = async () => {
    const invoice = await saveInvoice();

    if (!invoice?.hash) {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.error,
          message: 'Pay link could not be generated.',
        },
        ...prevState,
      ]);
      return;
    }

    setPaylinkHash(invoice.hash);

    try {
      await navigator.clipboard.writeText(window.location.origin + '/payment/' + invoice.hash);
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.success,
          message: 'Pay link successfully generated and added to your clipboard.',
        },
        ...prevState,
      ]);
    } catch {
      setMessages((prevState) => [
        {
          severity: AlertSeverityTypes.success,
          message: 'Pay link successfully generated. Could not add it to your clipboard.',
        },
        ...prevState,
      ]);
    }
  };

  const handlePaxSaleRequest = async (serialNum) => {
    if (!serialNum) {
      setMessages([
        { severity: AlertSeverityTypes.warning, message: 'Please select a Pax terminal before submitting.' },
      ]);
      return;
    }

    const invoice = await saveInvoice();

    if (!invoice?.id) {
      setMessages((prevState) => [
        { severity: AlertSeverityTypes.error, message: 'Could not send Pax sale request.' },
        ...prevState,
      ]);
      return;
    }

    setIsLoading(true);
    invoicesService
      .sendPaxSaleRequestWithInvoice(invoice.id, serialNum)
      .then(() => {
        setMessages([{ severity: AlertSeverityTypes.success, message: 'Pax sale request sent.' }]);
        setRedirectTo(REDIRECT_PATHS.INVOICES);
      })
      .catch((error) => {
        setMessages([
          {
            severity: AlertSeverityTypes.error,
            message: error?.response?.data?.message ?? 'Could not send Pax sale request.',
          },
        ]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSendPayLinkViaText = async () => {
    // Validate
    let isValid = true;
    let tempMessages = [];

    if (!selectedInvoice?.value) {
      isValid = false;
      tempMessages.push({
        severity: AlertSeverityTypes.error,
        message: 'Invoice is required. Please generate one through Pay Link or select one.',
      });
    }

    if (!customerPhone) {
      isValid = false;
      tempMessages.push({
        severity: AlertSeverityTypes.error,
        message: 'A phone number is required to send a text pay link.',
      });
    }

    if (!isValid) {
      setMessages(tempMessages);
      return;
    }

    const { id } = await saveInvoice(true, false);

    if (id) {
      setMessages((prevState) => [
        { severity: AlertSeverityTypes.success, message: `Text pay link sent to ${customerPhone}.` },
        ...prevState,
      ]);
    }
  };

  const handleSendPayLinkViaEmail = async () => {
    // Validate
    let isValid = true;
    let tempMessages = [];

    if (!selectedInvoice?.value) {
      isValid = false;
      tempMessages.push({
        severity: AlertSeverityTypes.error,
        message: 'Invoice is required. Please generate one through Pay Link or select one.',
      });
    }

    if (!customerEmail) {
      isValid = false;
      tempMessages.push({
        severity: AlertSeverityTypes.error,
        message: 'An email is required to send an email pay link.',
      });
    }

    if (!isValid) {
      setMessages(tempMessages);
      return;
    }

    const { id } = await saveInvoice(false, true);

    if (id) {
      setMessages((prevState) => [
        { severity: AlertSeverityTypes.success, message: `Email pay link sent to ${customerEmail}.` },
        ...prevState,
      ]);
    }
  };

  const handleSubmitPay = async () => {
    if (!areRequiredFieldsValid()) {
      setMessages([
        {
          severity: AlertSeverityTypes.error,
          message: 'Missing required data.',
        },
      ]);
      return;
    }

    if (selectedInvoice?.value) {
      await processPaymentOnInvoice(selectedInvoice?.value);
    } else {
      await processPaymentWithoutInvoice();
    }

    setIsLoading(false);
  };

  const handlePaymentTypeToggle = (event) => {
    setSelectedPaymentMethod({});
    setPaymentTypeToggle(event.target.value);
    setPaymentType(PAY_TYPE_DEFAULTS[event.target.value]);
    setMessages([]);
    setPayButtonLabel(PAY_BUTTON_LABELS[event.target.value]);
  };

  const handleChangeSaveCardCheckbox = (event) => {
    const isNowChecked = event?.target?.checked;
    if (selectedPaymentMethod?.gatewayPaymentMethodId) {
      // Can't change this while payment method is selected
      return;
    }
    if (isNowChecked && paymentGateway === commonStrings.multipass && tsepResponse?.status !== 'PASS') {
      // Validate and return early if doesn't pass
      return;
    }
    setIsPaymentMethodToBeSaved(isNowChecked);
  };

  const handleChangeSaveACHCheckbox = (event) => {
    const isNowChecked = event?.target?.checked;
    if (selectedPaymentMethod?.gatewayPaymentMethodId) {
      // Can't change this while payment method is selected
      return;
    }
    setIsPaymentMethodToBeSaved(isNowChecked);
  };

  const handleSelectAccount = (selectedAccountDetails) => {
    if (selectedAccountDetails?.gatewayPaymentMethodId) {
      let accountDetails = {
        ...selectedAccountDetails,
        accountNumber: selectedAccountDetails.maskedAccountNumber,
      };
      setSelectedPaymentMethod(accountDetails);
    } else {
      // if removing selected account
      setSelectedPaymentMethod({});
    }
  };

  return (
    <Container component="section" maxWidth={false}>
      <Backdrop open={isLoading} sx={{ zIndex: 9999, color: '#fff' }}>
        <CircularProgress style={{ color: 'white' }} />
      </Backdrop>
      {paymentGateway === 'multipass' && autoFillMultipassFields()}

      <QRModal
        open={isQRCodeModalOpen}
        handleClose={() => {
          setIsQRCodeModalOpen(false);
        }}
        GetPayLink={paylinkHash}
      />

      <Grid container rowSpacing={4} columnSpacing={4}>
        {messages?.length > 0 && (
          <Grid item xs={12} md={12}>
            <FlexAlerts messages={messages} />
          </Grid>
        )}
        <Grid item xs={12} lg={4} id="payment-details-section">
          <form id="DemoFormTerminal">
            <Stack spacing={2} alignItems="left">
              <ToggleButtonGroup
                exclusive
                color="primary"
                fullWidth
                value={paymentTypeToggle}
                onChange={handlePaymentTypeToggle}
              >
                <ToggleButton fullWidth value="card">
                  {`Credit/Debit${isDualPricingView ? '' : ' Card'}`}
                </ToggleButton>
                {achConfig.isACHEnabled && (
                  <ToggleButton fullWidth value="ach">
                    {`ACH${isDualPricingView ? '' : ' Payment'}`}
                  </ToggleButton>
                )}
                {isDualPricingView && (
                  <ToggleButton fullWidth value="cash">
                    Cash
                  </ToggleButton>
                )}
              </ToggleButtonGroup>
              {paymentTypeToggle !== commonStrings.cash && savedPaymentMethods?.length > 0 && (
                <SelectAccount
                  showType={paymentTypeToggle}
                  paymentMethods={savedPaymentMethods}
                  selectedAccount={selectedPaymentMethod}
                  selectAccount={handleSelectAccount}
                />
              )}
              {paymentTypeToggle === commonStrings.card && paymentGateway === commonStrings.multipass && (
                <TsepCardV2
                  key={`tsep-${reloadTsepCount}`}
                  displayCardDetails={cardDisplayData}
                  customerToken={selectedPaymentMethod?.gatewayPaymentMethodId || isPaymentMethodToBeSaved}
                  handleChangeCheckbox={handleChangeSaveCardCheckbox}
                />
              )}
              {paymentTypeToggle === commonStrings.card && paymentGateway === commonStrings.paytrace && (
                <PaytraceCard
                  card={card}
                  cardErrors={cardErrors}
                  cardRef={cardRef}
                  expiryDateRef={expiryDateRef}
                  cscCardRef={cscCardRef}
                  handleChangeCard={handleChangePaytraceCard}
                  displayCardDetails={cardDisplayData}
                  isSaveChecked={selectedPaymentMethod?.gatewayPaymentMethodId || isPaymentMethodToBeSaved}
                  handleChangeCheckbox={handleChangeSaveCardCheckbox}
                />
              )}
              {paymentTypeToggle === commonStrings.ach && achConfig.isACHEnabled && (
                <div>
                  <ACHForm
                    ach={achDetails}
                    handleChangeACH={handleChangeACH}
                    variant={achConfig.isExtraFieldsRequired ? 'detailedList' : 'basicListTight'}
                    location={achConfig}
                    ref={achFormValidationRef}
                    disabled={Object.keys(selectedPaymentMethod)?.length > 0}
                  />
                  {achConfig.isACHPaymentVaultEnabled && (selectedCustomer?.value || customerName) && (
                    <FormControlLabel
                      label="Save bank account for future use"
                      control={
                        <Checkbox
                          checked={selectedPaymentMethod?.gatewayPaymentMethodId || isPaymentMethodToBeSaved}
                          onChange={handleChangeSaveACHCheckbox}
                          color="primary"
                        />
                      }
                    />
                  )}
                </div>
              )}
            </Stack>
          </form>
        </Grid>

        <Grid item xs={12} lg={8} id="transaction-details-section">
          <Stack spacing={3}>
            <CreatableAutocomplete
              id="invoices-list"
              label={invoiceListConfig?.label}
              fullWidth
              value={selectedInvoice}
              options={invoicesList}
              setValue={setSelectedInvoice}
              required={invoiceListConfig?.required}
              error={fieldErrors?.invoice}
            />

            <CreatableAutocomplete
              id="customers-list"
              label="Customer Name"
              fullWidth
              value={selectedCustomer}
              options={customersList}
              setValue={setSelectedCustomer}
              required
              disabled={!!selectedInvoice?.customerId}
              error={fieldErrors?.customer}
            />

            <Stack spacing={2} direction="row">
              <AmountTextField
                id="base-amount"
                label={isDualPricingView ? 'Cash Amount' : 'Amount'}
                amount={baseAmount}
                setAmount={setBaseAmount}
                required
                fullWidth
                error={fieldErrors?.baseAmount}
              />
              {isDualPricingView && (
                <AmountTextField
                  id="card-subtotal"
                  label={'Card Amount'}
                  disabled
                  amount={displayCalculationResult?.card?.subTotal}
                  fullWidth
                />
              )}
            </Stack>

            <TextField
              id="customer-email"
              data-cy="customer-email"
              fullWidth
              label="Email"
              onChange={(event) => {
                setCustomerEmail(event?.target?.value);
              }}
              value={customerEmail}
            />
            {isPhoneNumberEnabled && (
              <TextField
                id="customer-phone"
                data-cy="customer-phone"
                fullWidth
                label="Phone Number"
                onChange={(event) => {
                  setCustomerPhone(event?.target?.value);
                }}
                value={customerPhone}
              />
            )}
            {!isDualPricingView && !calculationResult?.options?.taxLast && (
              <FeeAmountTextField
                id="tax"
                disabled
                fee={calculationResult.fees?.tax}
                databaseFeeSettings={userData?.settings}
              />
            )}
            {isDualPricingView ? (
              <Stack spacing={2} direction="row">
                <FeeAmountTextField
                  id="cash-surcharge"
                  labelPrefix="Cash"
                  disabled
                  fee={displayCalculationResult.cash?.fees?.surcharge}
                  databaseFeeSettings={userData?.settings}
                />
                <FeeAmountTextField
                  id="card-surcharge"
                  labelPrefix="Card"
                  disabled
                  fee={displayCalculationResult.card?.fees?.surcharge}
                  databaseFeeSettings={userData?.settings}
                />
              </Stack>
            ) : (
              <FeeAmountTextField
                id="surcharge"
                disabled
                fee={calculationResult.fees?.surcharge}
                databaseFeeSettings={userData?.settings}
              />
            )}
            {!isDualPricingView && (
              <>
                <FeeAmountTextField
                  id="card-upcharge"
                  disabled
                  fee={calculationResult.fees?.cardUpcharge}
                  databaseFeeSettings={userData?.settings}
                />
                {[commonStrings.ach, commonStrings.cash].includes(paymentType) && (
                  <FeeAmountTextField
                    id="cash-discount"
                    disabled
                    fee={calculationResult.fees?.cashDiscount}
                    databaseFeeSettings={userData?.settings}
                  />
                )}
              </>
            )}
            <AddressFieldsDropDown
              type="all"
              billingAddressFields={billingAddress}
              shippingAddressFields={shippingAddress}
              billingAddressSettings={userData?.settings?.virtualTerminalSettings?.billingAddress}
              shippingAddressSettings={userData?.settings?.virtualTerminalSettings?.shippingAddress}
              setBillingAddressFields={setBillingAddress}
              setShippingAddressFields={setShippingAddress}
              billingFormValidationRef={billingAddressFormValidationRef}
              shippingFormValidationRef={shippingAddressFormValidationRef}
              fieldAlignment={'column'}
            />
            {calculationResult?.options?.taxLast &&
              (isDualPricingView ? (
                <Stack spacing={2} direction="row">
                  <FeeAmountTextField
                    id="cash-tax"
                    labelPrefix="Cash"
                    disabled
                    fee={displayCalculationResult.cash?.fees?.tax}
                    databaseFeeSettings={userData?.settings}
                  />
                  <FeeAmountTextField
                    id="card-tax"
                    labelPrefix="Card"
                    disabled
                    fee={displayCalculationResult.card?.fees?.tax}
                    databaseFeeSettings={userData?.settings}
                  />
                </Stack>
              ) : (
                <FeeAmountTextField
                  id="tax"
                  disabled
                  fee={calculationResult.fees?.tax}
                  databaseFeeSettings={userData?.settings}
                />
              ))}
            {isDualPricingView ? (
              <Stack spacing={2} direction="row" id="dual-pricing-totals">
                <AmountTextField
                  id="cash-total"
                  label={'Cash Total'}
                  disabled
                  amount={displayCalculationResult.cash?.totalAmount}
                  fullWidth
                  error={fieldErrors?.totalAmount}
                  isBold={paymentTypeToggle !== 'card'}
                />
                <AmountTextField
                  id="card-total"
                  label={'Card Total'}
                  disabled
                  amount={displayCalculationResult.card?.totalAmount}
                  fullWidth
                  error={fieldErrors?.totalAmount}
                  isBold={paymentTypeToggle === 'card'}
                />
              </Stack>
            ) : (
              <AmountTextField
                id="total-amount"
                label={'Total'}
                disabled
                isBold
                amount={calculationResult.totalAmount}
                fullWidth
                error={fieldErrors?.totalAmount}
              />
            )}
            <TextField id="notes" fullWidth label="Notes" onChange={(e) => setNotes(e?.target?.value)} value={notes} />
          </Stack>
        </Grid>
        <Grid item xs={12} md={12} id="buttons-section">
          <Grid container rowSpacing={2} justifyContent={'space-between'}>
            <Grid item xs={12} md={'auto'}>
              <Grid container rowSpacing={2} columnSpacing={2} justifyContent={'flex-start'} id="left-side-buttons">
                <Grid item xs={12} md={'auto'}>
                  <Button
                    id="generate-qr-code-button"
                    fullWidth
                    variant="contained"
                    onClick={handleGenerateQRCode}
                    startIcon={<QrCode />}
                  >
                    QR Code
                  </Button>
                </Grid>
                <Grid item xs={12} md={'auto'}>
                  <Button
                    id="generate-paylink-button"
                    data-cy="terminal-pay-link-button"
                    fullWidth
                    variant="contained"
                    onClick={handleGeneratePaylink}
                    startIcon={<ContentCopy />}
                  >
                    Pay Link
                  </Button>
                </Grid>
                {paylinkHash && (
                  <Grid item xs={12} md={'auto'}>
                    <TextField
                      id="pay-link-text"
                      data-cy="pay-link-text"
                      fullWidth
                      disabled
                      size="small"
                      value={window.location.origin + '/payment/' + paylinkHash}
                    />
                  </Grid>
                )}
              </Grid>
            </Grid>
            <Grid item xs={12} md={'auto'}>
              <Grid container rowSpacing={2} columnSpacing={2} justifyContent={'flex-end'} id="right-side-buttons">
                {selectedInvoice?.value && (
                  <>
                    <Grid item xs={12} md={'auto'}>
                      <Tooltip placement="top" title={customerPhone ? '' : 'Phone number required.'}>
                        <span>
                          <Button
                            id="send-text-pay-link-button"
                            data-cy="send-text-pay-link-button"
                            fullWidth
                            variant="contained"
                            onClick={handleSendPayLinkViaText}
                            startIcon={<Chat />}
                            disabled={!customerPhone}
                          >
                            Text Invoice
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={12} md={'auto'}>
                      <Tooltip placement="top" title={customerEmail ? '' : 'Email required.'}>
                        <span>
                          <Button
                            id="send-email-pay-link-button"
                            data-cy="send-email-pay-link-button"
                            fullWidth
                            variant="contained"
                            onClick={handleSendPayLinkViaEmail}
                            startIcon={<Send />}
                            disabled={!customerEmail}
                          >
                            Email Invoice
                          </Button>
                        </span>
                      </Tooltip>
                    </Grid>
                  </>
                )}
                {isPaxTransactionsEnabled && (
                  <Grid item xs={12} md={'auto'}>
                    <PaxTerminalModal fullWidth onSubmit={handlePaxSaleRequest} paxTerminals={paxTerminals} />
                  </Grid>
                )}
                <Grid item xs={12} md={'auto'}>
                  <Button
                    id="process-payment-button"
                    disabled={submitPayIsDisabled}
                    fullWidth
                    variant="contained"
                    onClick={handleSubmitPay}
                  >
                    {payButtonLabel}
                  </Button>
                </Grid>
                <Grid item xs={12} md={'auto'}>
                  <BackButton fullWidth />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Container>
  );
};

export default CreditTab;
